import { inject, Injectable } from '@angular/core';
import { HttpContext } from '@angular/common/http';
import { lastValueFrom } from 'rxjs';
import { ToursV3Service } from 'api/addons-v3/services';
import { GetConsigneeAddonsToursV3$Params } from 'api/addons-v3/fn/tours-v-3/get-consignee-addons-tours-v-3';
import { SortType } from 'api-v3/models';
import { UserRoles } from 'api/models';
import { ToursStore } from '@app/stores/toursStore';
import { TourData } from '@app/shared/models/store-types';
import { SearchStore } from '@app/stores/searchStore';
import { AppStore } from '@app/stores/appStore';
import { ABORT_SIGNAL } from '@app/shared/constants/http-context-tokens';
import {
    getFullTextSearchParam,
    withColumnFilters,
} from '@app/shared/utils/paginated-search';

@Injectable({
    providedIn: 'root',
})
export class ConsigneeToursPaginatedService {
    private toursStore = inject(ToursStore);
    private searchStore = inject(SearchStore);
    private appStore = inject(AppStore);

    constructor(private toursService: ToursV3Service) {}

    async loadConsigneeToursPaginated(
        organizationSiteKey: string
    ): Promise<void> {
        // Prevent duplicate calls if already loading
        if (this.toursStore.loading()) {
            return;
        }

        this.toursStore.setToursLoading(true);
        this.toursStore.setToursError(null);

        // Cancel any ongoing requests and get new abort signal
        const abortSignal = this.searchStore.cancelOngoingRequests();

        try {
            const searchText = this.searchStore.searchText();
            const pagination = this.searchStore.pagination();
            const sort = this.searchStore.sort();
            const columnFilters = this.searchStore.columnFilters();

            // Get multi-site selection and site information for role-based read_only logic, and showClosed filter
            const currentSite = this.appStore.currentOrganizationSite();
            const selectedSiteKeys = this.appStore.getSelectedLocationKeys('consignee');
            const showClosed = this.appStore.showClosed();

            // Don't make API call if no organization sites are selected
            if (selectedSiteKeys.length === 0) {
                return;
            }

            // Check if request was cancelled before proceeding
            if (abortSignal?.aborted) {
                return;
            }

            const sortBy = sort.sortBy;

            // Use valid sort field or fall back to default - ensure type safety
            const validSortBy = sortBy as any;

            // Default to desc for dateCreated (newest first), use user's direction for other fields
            const sortDirection =
                validSortBy === 'dateCreated' && !sortBy
                    ? SortType.Desc // Default to newest first when no sort is specified
                    : sort.sortDirection === 'asc'
                      ? SortType.Asc
                      : SortType.Desc;

            // One query param per active column filter, AND-ed with each other
            // and with the full-text search by the backend. The explicit type
            // turns a param the generated client doesn't know into a compile
            // error instead of a silently dropped query param.
            const params = withColumnFilters<GetConsigneeAddonsToursV3$Params>(
                {
                    organizationSiteKey,
                    organizationSiteKeys:
                        selectedSiteKeys.length > 0
                            ? selectedSiteKeys
                            : undefined,
                    limit: pagination.limit,
                    offset: pagination.offset,
                    sort: sortDirection,
                    sortBy: validSortBy,
                    filterNotClosed: !showClosed,
                    search: getFullTextSearchParam(searchText),
                },
                columnFilters
            );

            // Check if request was already cancelled before making API call
            if (abortSignal?.aborted) {
                return;
            }

            // Create HttpContext with abort signal for proper HTTP request cancellation
            const context = new HttpContext().set(ABORT_SIGNAL, abortSignal);

            // This will go through the HTTP interceptors and can be cancelled
            const response = await lastValueFrom(
                this.toursService.getConsigneeAddonsToursV3(params, context)
            );

            if (response?.data) {
                // Map the response to include orgSiteKey and ensure proper typing
                const toursWithOrgSiteKey: TourData[] = response.data.map(
                    tour => ({
                        ...tour,
                        orgSiteKey: organizationSiteKey,
                        read_only: currentSite
                            ? currentSite.roles.includes(
                                  UserRoles.RemoteConsignee
                              ) &&
                              !currentSite.roles.includes(UserRoles.Consignee)
                            : false,
                    })
                );

                this.toursStore.setTours(toursWithOrgSiteKey);

                // Update pagination info in centralized searchStore
                this.searchStore.updatePaginationFromApiResponse(response.paginationInfo);
            }
        } catch (error: any) {
            // Extract request ID from error if available
            const requestId =
                error?.headers?.get?.('x-request-id') ||
                error?.error?.requestId ||
                null;
            const errorMessage = requestId
                ? `GENERAL.ERRORS.FAILED_TO_LOAD_CONSIGNEE_TOURS|${requestId}`
                : 'GENERAL.ERRORS.FAILED_TO_LOAD_CONSIGNEE_TOURS';

            this.toursStore.clearTours();
            this.toursStore.setToursError(errorMessage);

            // Reset pagination to empty state on error
            this.searchStore.resetPaginationToEmpty();
        } finally {
            this.toursStore.setToursLoading(false);
        }
    }
}
